// @ts-nocheck
"use client";

import { TrendingUp, TrendingDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { StrategySignal } from "@/types/trade/strategy-signals";

interface StrategyPositionDisplayProps {
  strategyType: string;
  openPositions: StrategySignal[];
  closedPositions: StrategySignal[];
  currentPrice: number;
}

export function StrategyPositionDisplay({
  strategyType,
  openPositions,
  closedPositions,
  currentPrice,
}: StrategyPositionDisplayProps) {
  console.log('[StrategyPositionDisplay] Props:', { strategyType, openPositions: openPositions?.length ?? 0, currentPrice });
  
  if (!openPositions || openPositions.length === 0) {
    console.log('[StrategyPositionDisplay] No open positions, not rendering');
    return null;
  }

  // Calculate total position size
  const totalSize = openPositions.reduce((sum, pos) => sum + pos.quantity, 0);
  
  // Calculate average entry price
  const avgEntryPrice = openPositions.reduce((sum, pos) => sum + pos.price * pos.quantity, 0) / totalSize;
  
  // Calculate current PnL
  const currentPnL = openPositions.reduce((sum, pos) => {
    const pnl = (currentPrice - pos.price) * pos.quantity;
    return sum + pnl;
  }, 0);
  
  const pnlPercent = ((currentPrice - avgEntryPrice) / avgEntryPrice) * 100;
  const isProfit = currentPnL >= 0;

  // Determine position type based on strategy
  const positionType = openPositions[0]?.type === 'BUY' ? 'Long' : 'Short';

  // Calculate margin usage (simplified)
  const marginUsage = (totalSize * avgEntryPrice * 0.01).toFixed(2); // Assuming 1% margin

  // Calculate stop loss and take profit levels
  const stopLoss = getStrategyStopLoss(strategyType, avgEntryPrice, positionType);
  const takeProfit = getStrategyTakeProfit(strategyType, avgEntryPrice, positionType);

  return (
    <div className="flex items-center space-x-1.5">
      {/* Position Type */}
      <div className="flex items-center space-x-0.5">
        <span className="text-[10px] text-zinc-500">Type</span>
        <span className={cn(
          "text-xs font-semibold",
          positionType === 'Long' ? "text-green-400" : "text-red-400"
        )}>
          {positionType}
        </span>
      </div>

      <div className="h-3 w-px bg-zinc-700/50" />

      {/* Size */}
      <div className="flex items-center space-x-0.5">
        <span className="text-[10px] text-zinc-500">Size</span>
        <span className="text-xs font-semibold text-white">
          {totalSize.toFixed(2)}
        </span>
      </div>

      <div className="h-3 w-px bg-zinc-700/50" />

      {/* Entry Price */}
      <div className="flex items-center space-x-0.5">
        <span className="text-[10px] text-zinc-500">Entry</span>
        <span className="text-xs font-semibold text-white">
          {avgEntryPrice.toFixed(2)}
        </span>
      </div>

      <div className="h-3 w-px bg-zinc-700/50" />

      {/* Margin Usage */}
      <div className="flex items-center space-x-0.5">
        <span className="text-[10px] text-zinc-500">Margin</span>
        <span className="text-xs font-semibold text-white">
          {marginUsage}%
        </span>
      </div>

      <div className="h-3 w-px bg-zinc-700/50" />

      {/* Step-by-step progress visualization */}
      <div className="flex items-center space-x-1">
        {/* Stop Loss Label */}
        <div className="text-[10px] text-red-400 font-medium">
          SL {stopLoss?.[0]?.toFixed(0)}
        </div>

        {/* Step Progress Bar (6 steps) - Clone style from screenshot */}
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5, 6].map((step) => {
            // Calculate which steps should be completed based on P&L
            // For LONG: progress towards TP
            // For SHORT: progress towards TP
            const targetPercent = 0.5; // Target is +0.5%
            const currentProgress = (Math.abs(pnlPercent) / targetPercent) * 100;
            const stepsCompleted = Math.floor((currentProgress / 100) * 6);
            const isCompleted = step <= stepsCompleted;
            
            return (
              <div
                key={step}
                className={cn(
                  "h-4 w-8 rounded-full transition-all",
                  isCompleted
                    ? positionType === 'Long'
                      ? "bg-green-500"
                      : "bg-red-500"
                    : "bg-zinc-700"
                )}
              />
            );
          })}
        </div>

        {/* Take Profit Label */}
        <div className="text-[10px] text-green-400 font-medium">
          TP {takeProfit?.[0]?.toFixed(0)}
        </div>
      </div>

      <div className="h-3 w-px bg-zinc-700/50" />

      {/* Compact PnL */}
      <div className="flex items-center space-x-0.5">
        <span className="text-[10px] text-zinc-500">PNL</span>
        <span className={cn(
          "text-xs font-bold",
          isProfit ? "text-green-400" : "text-red-400"
        )}>
          {isProfit ? '+' : ''}{currentPnL.toFixed(2)} ({pnlPercent >= 0 ? '+' : ''}{pnlPercent.toFixed(2)}%)
        </span>
      </div>
    </div>
  );
}

// Helper functions to get strategy-specific stop loss and take profit levels
function getStrategyStopLoss(strategyType: string, entryPrice: number, positionType: string): number[] {
  const strategies: Record<string, number> = {
    'otc': 0.002, // 0.2%
    'ai-agents': 0.0025, // 0.25%
    'pivot-reversal': 0.004, // 0.4%
    'smart-money-concepts': 0.004, // 0.4%
    'vwap-channel': 0.003, // 0.3%
    'breakout-channels': 0.0035, // 0.35%
    'candlestick': 0.003, // 0.3%
  };

  const slPercent = strategies[strategyType] || 0.003;
  const sl1 = positionType === 'Long' 
    ? entryPrice * (1 - slPercent)
    : entryPrice * (1 + slPercent);
  const sl2 = positionType === 'Long'
    ? entryPrice * (1 - slPercent * 1.5)
    : entryPrice * (1 + slPercent * 1.5);

  return [sl1, sl2];
}

function getStrategyTakeProfit(strategyType: string, entryPrice: number, positionType: string): number[] {
  const strategies: Record<string, number> = {
    'otc': 0.003, // 0.3%
    'ai-agents': 0.004, // 0.4%
    'pivot-reversal': 0.006, // 0.6%
    'smart-money-concepts': 0.006, // 0.6%
    'vwap-channel': 0.005, // 0.5%
    'breakout-channels': 0.007, // 0.7%
    'candlestick': 0.005, // 0.5%
  };

  const tpPercent = strategies[strategyType] || 0.005;
  const tp1 = positionType === 'Long'
    ? entryPrice * (1 + tpPercent)
    : entryPrice * (1 - tpPercent);
  const tp2 = positionType === 'Long'
    ? entryPrice * (1 + tpPercent * 1.5)
    : entryPrice * (1 - tpPercent * 1.5);

  return [tp1, tp2];
}

